import React, { useRef, useState, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';
import { X, Camera, AlertCircle, Loader2, Hand } from 'lucide-react';
import * as handpose from '@tensorflow-models/handpose';
import '@tensorflow/tfjs';

interface WebcamPalmReaderProps {
  onCapture: (imageData: string) => void;
  onCancel: () => void;
}

const WebcamPalmReader: React.FC<WebcamPalmReaderProps> = ({ onCapture, onCancel }) => {
  const webcamRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const [model, setModel] = useState<handpose.HandPose | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isHandDetected, setIsHandDetected] = useState(false);
  const [isHandAligned, setIsHandAligned] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const requestAnimationRef = useRef<number>();
  const alignmentTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load the handpose model
  useEffect(() => {
    const loadModel = async () => {
      try {
        setIsModelLoading(true);
        const loadedModel = await handpose.load();
        setModel(loadedModel);
        setIsModelLoading(false);
        console.log('Handpose model loaded');
      } catch (err) {
        console.error('Error loading handpose model:', err);
        setError('Failed to load hand detection model. Please try again or use the manual upload option.');
        setIsModelLoading(false);
      }
    };

    loadModel();

    return () => {
      if (requestAnimationRef.current) {
        cancelAnimationFrame(requestAnimationRef.current);
      }
      if (alignmentTimerRef.current) {
        clearTimeout(alignmentTimerRef.current);
      }
    };
  }, []);

  // Update dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      if (webcamRef.current && webcamRef.current.video) {
        const video = webcamRef.current.video;
        setDimensions({
          width: video.videoWidth,
          height: video.videoHeight
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle webcam ready state
  const handleWebcamReady = () => {
    setIsCameraReady(true);
    if (webcamRef.current && webcamRef.current.video) {
      const video = webcamRef.current.video;
      setDimensions({
        width: video.videoWidth,
        height: video.videoHeight
      });
    }
  };

  // Detect hands in the webcam feed
  const detectHands = useCallback(async () => {
    if (
      model &&
      webcamRef.current &&
      webcamRef.current.video &&
      webcamRef.current.video.readyState === 4 &&
      canvasRef.current
    ) {
      const video = webcamRef.current.video;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      try {
        // Detect hands
        const predictions = await model.estimateHands(video);
        
        if (predictions.length > 0) {
          setIsHandDetected(true);
          
          // Draw hand landmarks
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Draw palm outline
            const palmPoints = predictions[0].landmarks.filter((_, i) => 
              [0, 1, 2, 5, 9, 13, 17].includes(i)
            );
            
            if (palmPoints.length >= 7) {
              ctx.beginPath();
              ctx.moveTo(palmPoints[0][0], palmPoints[0][1]);
              for (let i = 1; i < palmPoints.length; i++) {
                ctx.lineTo(palmPoints[i][0], palmPoints[i][1]);
              }
              ctx.closePath();
              ctx.strokeStyle = 'rgba(0, 255, 0, 0.7)';
              ctx.lineWidth = 3;
              ctx.stroke();
              
              // Check if hand is aligned with center
              const centerX = canvas.width / 2;
              const centerY = canvas.height / 2;
              
              // Calculate palm center
              const palmCenter = palmPoints.reduce(
                (acc, point) => [acc[0] + point[0], acc[1] + point[1]],
                [0, 0]
              );
              palmCenter[0] /= palmPoints.length;
              palmCenter[1] /= palmPoints.length;
              
              // Calculate distance from center
              const distance = Math.sqrt(
                Math.pow(palmCenter[0] - centerX, 2) + 
                Math.pow(palmCenter[1] - centerY, 2)
              );
              
              // Check if palm is centered and open
              const isAligned = distance < canvas.width * 0.15;
              setIsHandAligned(isAligned);
              
              // Draw alignment indicator
              ctx.beginPath();
              ctx.arc(centerX, centerY, canvas.width * 0.15, 0, 2 * Math.PI);
              ctx.strokeStyle = isAligned ? 'rgba(0, 255, 0, 0.5)' : 'rgba(255, 0, 0, 0.5)';
              ctx.lineWidth = 2;
              ctx.stroke();
              
              // Start countdown if hand is aligned
              if (isAligned) {
                if (!alignmentTimerRef.current) {
                  alignmentTimerRef.current = setTimeout(() => {
                    captureImage();
                    if (alignmentTimerRef.current) {
                      clearTimeout(alignmentTimerRef.current);
                      alignmentTimerRef.current = null;
                    }
                  }, 2000);
                  
                  // Start countdown
                  setCountdown(2);
                  const countdownInterval = setInterval(() => {
                    setCountdown((prev) => {
                      if (prev === null || prev <= 1) {
                        clearInterval(countdownInterval);
                        return null;
                      }
                      return prev - 1;
                    });
                  }, 1000);
                }
              } else {
                // Reset countdown if hand is no longer aligned
                if (alignmentTimerRef.current) {
                  clearTimeout(alignmentTimerRef.current);
                  alignmentTimerRef.current = null;
                  setCountdown(null);
                }
              }
            }
          }
        } else {
          setIsHandDetected(false);
          setIsHandAligned(false);
          
          // Reset countdown if no hand is detected
          if (alignmentTimerRef.current) {
            clearTimeout(alignmentTimerRef.current);
            alignmentTimerRef.current = null;
            setCountdown(null);
          }
          
          // Clear canvas
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Draw target area
            ctx.beginPath();
            ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width * 0.15, 0, 2 * Math.PI);
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        }
      } catch (err) {
        console.error('Error detecting hands:', err);
      }
      
      // Continue detection loop
      requestAnimationRef.current = requestAnimationFrame(detectHands);
    } else {
      // If not ready yet, try again in the next frame
      requestAnimationRef.current = requestAnimationFrame(detectHands);
    }
  }, [model]);

  // Start hand detection when model and camera are ready
  useEffect(() => {
    if (model && isCameraReady && !isModelLoading) {
      detectHands();
    }
  }, [model, isCameraReady, isModelLoading, detectHands]);

  // Capture the image when hand is aligned
  const captureImage = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        onCapture(imageSrc);
      }
    }
  }, [onCapture]);

  // Toggle camera between front and back
  const toggleCamera = () => {
    setFacingMode(prevMode => prevMode === 'user' ? 'environment' : 'user');
  };

  return (
    <div className="relative flex flex-col items-center">
      <div className="relative w-full max-w-md">
        {/* Camera feed */}
        <div className="relative rounded-lg overflow-hidden bg-black">
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            videoConstraints={{
              facingMode,
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }}
            onUserMedia={handleWebcamReady}
            className="w-full h-auto"
            style={{ display: isCameraReady ? 'block' : 'none' }}
          />
          
          {/* Canvas overlay for hand tracking */}
          <canvas
            ref={canvasRef}
            className="absolute top-0 left-0 w-full h-full"
          />
          
          {/* Loading indicator */}
          {(isModelLoading || !isCameraReady) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-70 text-white">
              <Loader2 className="w-12 h-12 animate-spin mb-4" />
              <p>{isModelLoading ? 'Loading hand detection model...' : 'Starting camera...'}</p>
            </div>
          )}
          
          {/* Hand alignment guide */}
          {isCameraReady && !isModelLoading && !isHandDetected && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="w-1/2 h-1/2 border-2 border-dashed border-white rounded-full opacity-50 flex items-center justify-center">
                <Hand className="w-16 h-16 text-white opacity-70" />
              </div>
              <p className="text-white mt-4 bg-black bg-opacity-50 p-2 rounded">
                Show your palm to the camera
              </p>
            </div>
          )}
          
          {/* Countdown indicator */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-black bg-opacity-50 text-white text-5xl font-bold rounded-full w-20 h-20 flex items-center justify-center">
                {countdown}
              </div>
            </div>
          )}
          
          {/* Status indicators */}
          <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center">
            <div className={`px-3 py-1 rounded-full text-sm ${isHandDetected ? 'bg-green-500' : 'bg-red-500'} text-white`}>
              {isHandDetected ? 'Hand Detected' : 'No Hand Detected'}
            </div>
            <div className={`px-3 py-1 rounded-full text-sm ${isHandAligned ? 'bg-green-500' : 'bg-yellow-500'} text-white`}>
              {isHandAligned ? 'Aligned' : 'Center Your Palm'}
            </div>
          </div>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
            <AlertCircle className="w-5 h-5 mr-2" />
            <p>{error}</p>
          </div>
        )}
        
        {/* Controls */}
        <div className="mt-4 flex justify-between">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <X className="w-5 h-5 mr-2 inline" />
            Cancel
          </button>
          
          <button
            onClick={toggleCamera}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Camera className="w-5 h-5 mr-2 inline" />
            Switch Camera
          </button>
          
          <button
            onClick={captureImage}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Capture Now
          </button>
        </div>
      </div>
      
      <div className="mt-6 text-center max-w-md">
        <h3 className="text-lg font-medium mb-2">Instructions</h3>
        <ul className="text-sm text-gray-600 space-y-2 text-left list-disc pl-5">
          <li>Hold your palm flat and face it toward the camera</li>
          <li>Center your palm within the circle guide</li>
          <li>Hold steady for 2 seconds for automatic capture</li>
          <li>Ensure good lighting for best results</li>
          <li>Use the "Switch Camera" button if needed</li>
        </ul>
      </div>
    </div>
  );
};

export default WebcamPalmReader;